import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { maxLength, required, Validators, minValue } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/internal-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistInternal } from 'src/store/actions/internal-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { IProvider } from '../../../../../types/provider';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { FieldDropdown } from '../../../../../components/redux-form/fields/FieldDropdown';

export interface InternalFormData {
    id: string | number;
    internalDate: string;
    expenseDate: string;
    amount: number;
    month: { monthEnd: string; monthStart: string };
    provider: string | number | Partial<IProvider>;
    description: string;
    note: string;
    providerDropDown: SelectObject | undefined;
}

interface InternalFormProps {
    providers: Partial<IProvider>[];
}

type InjectedProps = InjectedFormProps<InternalFormData, InternalFormProps>;

const validators: Validators<InternalFormData> = {
    internalDate: [required({ message: 'Internal Date is required.' }), maxLength({ max: 255 })],
    amount: [required({ message: 'Amount is required.' }), minValue({ min: 1 }), maxLength({ max: 255 })],
    providerDropDown: [required({ message: 'Provider is required.' }), maxLength({ max: 255 })],
    description: [required({ message: 'Description is required.' }), maxLength({ max: 255 })],
    note: [required({ message: 'Note is required.' }), maxLength({ max: 255 })],
};

const InternalForm: React.FunctionComponent<InjectedProps & InternalFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    providers,
}: InjectedProps & InternalFormProps) => {
    const isNew = !initialValues || !initialValues.id;
    const providerValues: SelectObject[] = [];
    providers.forEach((prov: Partial<IProvider>) => {
        providerValues.push({ value: prov.id, label: `${prov.fullName}` });
    });
    providerValues.sort((a: SelectObject, b: SelectObject) => {
        const textA = a.label.toUpperCase();
        const textB = b.label.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <div className="d-flex flex-row">
                <div className="w-50 mr-5">
                    <FieldDate
                        name="internalDate"
                        validate={validators.internalDate}
                        type="text"
                        date={true}
                        disabled={!isNew}
                        wrapperConfig={{
                            label: 'Internal Date: *',
                        }}
                    />
                </div>
                <div className="w-50">
                    <FieldInput
                        name="amount"
                        type="number"
                        validate={validators.amount}
                        wrapperConfig={{
                            label: 'Amount: *',
                        }}
                    />
                </div>
            </div>
            <div className="d-flex flex-row">
                <div className="w-50 mr-5">
                    <FieldInput
                        name="description"
                        type="text"
                        validate={validators.description}
                        wrapperConfig={{
                            label: 'Description *',
                        }}
                    />
                </div>
                <div className="w-50">
                    <FieldInput
                        name="note"
                        type="text"
                        validate={validators.note}
                        wrapperConfig={{
                            label: 'Note: *',
                        }}
                    />
                </div>
            </div>
            <FieldDropdown
                name="providerDropDown"
                data={providerValues}
                textField={'label'}
                disabled={!isNew}
                valueField={'value'}
                validate={validators.providerDropDown}
                wrapperConfig={{
                    label: 'Provider: *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.INTERNALS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<InternalFormData, InternalFormProps>({
    form: 'internal-form',
    onSubmit: async (data: InternalFormData, dispatch: Dispatch<any>, props: InternalFormProps) => {
        await asyncPersistInternal(data, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<InternalFormData, InternalFormProps, string>,
    ) {
        //const isNew = !props.initialValues || !props.initialValues.id;
        //toast.success(`Internal was successfully ${isNew ? 'created' : 'updated'}.`);
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(InternalForm);
